/*
 * File: resources.js
 * Project: our-wave-stories-app
 *
 * Created by Brendan Michaelsen on May 18, 2025 at 1:17 AM
 * Copyright © 2025 Our Wave, Inc. All rights reserved.
 *
 * Last Modified: May 26, 2025 at 3:27 PM
 * Modified By: Brendan Michaelsen
 */

/**
 * Imports
 */

// Modules
import React from 'react';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { Trans } from 'react-i18next';

// Utilities
import { formatPhoneNumber } from '../../utilities/phone';

// Components
import { ActionModule } from '../components/ActionModule';
import { LocaleLink } from '../components/LocaleLink';
import { Typography } from '../components/Typography';


/**
 * Utilities
 */

export const renderPrimaryResource = (primaryResource, locale, t) => {

	// Get translation key
	let translationKey = '';
	if (primaryResource?.has_phone || primaryResource?.has_sms) {
		if (primaryResource?.has_url) {
			if (primaryResource?.prompt_use_the) {
				translationKey = 'primaryPromptContactNounLink';
			} else {
				translationKey = 'primaryPromptContactLink';
			}
		} else if (primaryResource?.prompt_use_the) {
			translationKey = 'primaryPromptContactNoun';
		} else {
			translationKey = 'primaryPromptContact';
		}
	} else if (primaryResource?.has_url) {
		if (primaryResource?.prompt_use_the) {
			translationKey = 'primaryPromptVisitNounLink';
		} else {
			translationKey = 'primaryPromptVisitLink';
		}
	} else if (primaryResource?.prompt_use_the) {
		translationKey = 'primaryPromptVisitNoun';
	} else {
		translationKey = 'primaryPromptVisit';
	}

	// Create primary action
	const buildPrimaryAction = () => {
		if (primaryResource) {
			if (primaryResource.has_phone) {
				return (
					<LocaleLink to={`tel:${primaryResource.primary_phone_number}`} target="_blank">
						<ActionModule center className="fontSemibold">
							<FontAwesomeIcon icon={['far', 'phone']} />
							<Typography tag="span">{formatPhoneNumber(primaryResource.primary_phone_number)}</Typography>
						</ActionModule>
					</LocaleLink>
				);
			} if (primaryResource.has_sms) {
				return (
					<LocaleLink to={`sms:${primaryResource.primary_sms_number}`} target="_blank">
						<ActionModule center className="fontSemibold">
							<FontAwesomeIcon icon={['far', 'message']} />
							<Typography tag="span">{formatPhoneNumber(primaryResource.primary_sms_number)}</Typography>
						</ActionModule>
					</LocaleLink>
				);
			} if (primaryResource.has_url) {
				return (
					<LocaleLink to={primaryResource.primary_url_locales[locale.localeBaseId]} target="_blank">
						<ActionModule center className="fontSemibold">
							<FontAwesomeIcon icon={['far', 'arrow-up-right']} />
							<Typography tag="span">
								<Trans t={t} i18nKey="actions.visit">
									Visit
								</Trans>
							</Typography>
						</ActionModule>
					</LocaleLink>
				);
			}
		}
		return (
			<ActionModule center className="shimmer shimmer-element fontSemibold">
				<FontAwesomeIcon icon={['far', 'arrow-up-right']} />
				<Typography tag="span" />
			</ActionModule>
		);
	};

	// Render component
	return (
		<>
			{/* Resource Prompt */}
			<Typography tag="p" variation="3" weight="semibold" className={!primaryResource && 'shimmer shimmer-element'} center>
				<Trans t={t} i18nKey={`resources.${translationKey}`}>
					For immediate help, contact the
					{' '}
					{primaryResource?.has_url
						? (
							<LocaleLink
								to={primaryResource?.primary_url_locales[locale.localeBaseId]}
								target="_blank"
							>
								{{ resource: primaryResource?.name_locales[locale.localeBaseId] }}
							</LocaleLink>
						)
						: { resource: primaryResource?.name_locales[locale.localeBaseId] } }
				</Trans>
			</Typography>

			{/* Resource Action */}
			{buildPrimaryAction()}
		</>
	);
};
